import { useCallback, useMemo, useState } from "react";
import {
  normalizeDebugTraceEvent,
  sortDebugTraceEvents,
  type DebugTraceCategory,
  type DebugTraceEvent,
  type DebugTraceLevel,
  type DebugTraceSource
} from "@/shared/types/debug-trace";

export interface DebugTraceFilters {
  source?: DebugTraceSource | "all";
  category?: DebugTraceCategory | "all";
  level?: DebugTraceLevel | "all";
  turnId?: string;
  query?: string;
}

export interface DebugTraceStats {
  total: number;
  bySource: Record<DebugTraceSource, number>;
  byCategory: Record<DebugTraceCategory, number>;
  byLevel: Record<DebugTraceLevel, number>;
}

interface UseDebugTraceOptions {
  maxEvents?: number;
}

const DEFAULT_MAX_DEBUG_EVENTS = 1000;

function compareDebugTraceEvents(a: DebugTraceEvent, b: DebugTraceEvent): number {
  const timeA = Date.parse(a.timestamp);
  const timeB = Date.parse(b.timestamp);

  if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
  if (Number.isNaN(timeA)) return 1;
  if (Number.isNaN(timeB)) return -1;
  if (timeA !== timeB) return timeA - timeB;

  return a.id.localeCompare(b.id);
}

function trimToMaxEvents(
  events: DebugTraceEvent[],
  maxEvents: number
): DebugTraceEvent[] {
  if (events.length <= maxEvents) {
    return events;
  }

  return events.slice(events.length - maxEvents);
}

function appendEventEfficiently(
  prev: DebugTraceEvent[],
  incoming: DebugTraceEvent,
  maxEvents: number
): DebugTraceEvent[] {
  const last = prev[prev.length - 1];

  if (!last || compareDebugTraceEvents(last, incoming) <= 0) {
    return trimToMaxEvents([...prev, incoming], maxEvents);
  }

  const sorted = sortDebugTraceEvents([...prev, incoming]);
  return trimToMaxEvents(sorted, maxEvents);
}

export function useDebugTrace(options?: UseDebugTraceOptions) {
  const maxEvents = options?.maxEvents ?? DEFAULT_MAX_DEBUG_EVENTS;
  const [events, setEvents] = useState<DebugTraceEvent[]>([]);

  const appendEvent = useCallback((event: Partial<DebugTraceEvent>) => {
    const normalized = normalizeDebugTraceEvent(event);
    setEvents((prev) => appendEventEfficiently(prev, normalized, maxEvents));
    return normalized;
  }, [maxEvents]);

  const appendEvents = useCallback((incoming: Partial<DebugTraceEvent>[]) => {
    if (!incoming.length) return;

    const normalized = incoming.map((event) => normalizeDebugTraceEvent(event));
    const sortedIncoming = sortDebugTraceEvents(normalized);

    setEvents((prev) => {
      const lastExisting = prev[prev.length - 1];
      const firstIncoming = sortedIncoming[0];

      if (
        !lastExisting ||
        !firstIncoming ||
        compareDebugTraceEvents(lastExisting, firstIncoming) <= 0
      ) {
        return trimToMaxEvents([...prev, ...sortedIncoming], maxEvents);
      }

      return trimToMaxEvents(
        sortDebugTraceEvents([...prev, ...sortedIncoming]),
        maxEvents
      );
    });
  }, [maxEvents]);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  const getFilteredEvents = useCallback(
    (filters: DebugTraceFilters): DebugTraceEvent[] => {
      return events.filter((event) => {
        if (filters.source && filters.source !== "all" && event.source !== filters.source) {
          return false;
        }

        if (
          filters.category &&
          filters.category !== "all" &&
          event.category !== filters.category
        ) {
          return false;
        }

        if (filters.level && filters.level !== "all" && event.level !== filters.level) {
          return false;
        }

        if (filters.turnId && event.turnId !== filters.turnId) {
          return false;
        }

        if (filters.query) {
          const normalizedQuery = filters.query.toLowerCase();
          const searchable = JSON.stringify(event).toLowerCase();
          if (!searchable.includes(normalizedQuery)) {
            return false;
          }
        }

        return true;
      });
    },
    [events]
  );

  const groupedByTurn = useMemo(() => {
    const map = new Map<string, DebugTraceEvent[]>();

    for (const event of events) {
      const key = event.turnId || "session";
      const group = map.get(key) ?? [];
      group.push(event);
      map.set(key, group);
    }

    return map;
  }, [events]);

  const stats = useMemo<DebugTraceStats>(() => {
    const bySource: Record<DebugTraceSource, number> = {
      client: 0,
      server: 0
    };

    const byCategory: Record<DebugTraceCategory, number> = {
      auth: 0,
      chat: 0,
      llm: 0,
      tool: 0,
      entity: 0,
      stream: 0,
      error: 0
    };

    const byLevel: Record<DebugTraceLevel, number> = {
      info: 0,
      warn: 0,
      error: 0
    };

    for (const event of events) {
      bySource[event.source] += 1;
      byCategory[event.category] += 1;
      byLevel[event.level] += 1;
    }

    return {
      total: events.length,
      bySource,
      byCategory,
      byLevel
    };
  }, [events]);

  return {
    events,
    appendEvent,
    appendEvents,
    clearEvents,
    getFilteredEvents,
    groupedByTurn,
    stats
  };
}
